import {
  getUsersFindAllQueryKey,
  useReferentialsFindAll,
  useUsersCreate,
  useUsersFindAll,
  useUsersRemove,
  useUsersUpdate,
} from '@api/WhisApi/generated';
import type { UserDto } from '@api/WhisApi/generated/model/userDto';
import type { UsersFindAllParams } from '@api/WhisApi/generated/model/usersFindAllParams';
import { Button } from '@components/Button/Button';
import { EmptyStateView } from '@components/EmptyStateView/EmptyStateView';
import { Input } from '@components/Input/Input';
import { LoadingView } from '@components/LoadingView/LoadingView';
import { Pagination } from '@components/Pagination/Pagination';
import { ScreenHeader } from '@components/ScreenHeader';
import { TabScreenContainer } from '@components/TabScreenContainer';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useAuth } from '@hooks/useAuth';
import { useIsWeb } from '@hooks/useIsWeb';
import type { UserFormValues } from '@screens/UserManagement/components/UserForm/userForm.types';
import { UserManagementModal } from '@screens/UserManagement/components/UserManagementModal';
import { UserManagementTable } from '@screens/UserManagement/components/UserManagementTable';
import {
  USER_MANAGEMENT_PAGE_SIZE,
  USER_MANAGEMENT_SEARCH_DEBOUNCE_MS,
  USER_MANAGEMENT_SORT_KEY_MAP,
} from '@screens/UserManagement/userManagement.constants';
import { getLocalizedLabel } from '@screens/UserManagement/userManagement.utils';
import { useQueryClient } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { canManageUsers, isUserDelegate } from '@utils/userPermissions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, SizableText, YStack } from 'tamagui';

export default function UserManagementScreen(): React.ReactNode {
  const { t, language } = useAppTranslation();
  const { user } = useAuth();
  const isWeb = useIsWeb();
  const isDelegate = isUserDelegate(user);
  const canUpdate = isWeb && canManageUsers(user);
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [activeUser, setActiveUser] = useState<UserDto | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const sortCriteria = useMemo(() => {
    if (sorting.length === 0) {
      return [];
    }

    return sorting
      .map((sort) => ({
        id: USER_MANAGEMENT_SORT_KEY_MAP[sort.id] ?? sort.id,
        order: sort.desc ? 'desc' : 'asc',
      }))
      .filter((sort) => Boolean(sort.id));
  }, [sorting]);

  const params = useMemo<UsersFindAllParams>(() => {
    const baseParams: Record<string, string | number | undefined> = {
      page,
      limit: USER_MANAGEMENT_PAGE_SIZE,
      search: searchValue.trim() || undefined,
    };

    sortCriteria.forEach((sort, index) => {
      baseParams[`sortBy[${index}][id]`] = sort.id;
      baseParams[`sortBy[${index}][order]`] = sort.order;
    });

    if (isDelegate && user?.country?.code) {
      return { ...baseParams, countryCode: user.country.code };
    }

    return baseParams;
  }, [isDelegate, page, searchValue, sortCriteria, user?.country?.code]);

  const { data: usersResponse, isPending } = useUsersFindAll(params, {
    query: {
      enabled: isWeb,
      staleTime: 60 * 1000,
      placeholderData: (previous) => previous,
    },
  });

  const { mutate: createUser } = useUsersCreate();
  const { mutate: updateUser } = useUsersUpdate();
  const { mutate: removeUser } = useUsersRemove();

  const { data: referentials } = useReferentialsFindAll({
    query: {
      enabled: isWeb,
      staleTime: 5 * 60 * 1000,
    },
  });

  const countryOptions = useMemo(() => {
    const baseOptions =
      referentials?.countries?.map((country) => ({
        value: country.code,
        label: getLocalizedLabel(country.name, language) || country.code,
      })) ?? [];

    return [
      { value: 'all', label: t('userManagement.filters.allCountries') },
      ...baseOptions,
    ];
  }, [referentials?.countries, language, t]);

  const users = usersResponse?.users ?? [];

  const totalPages = useMemo(() => {
    const count = usersResponse?.count ?? totalCount ?? 0;
    return Math.max(1, Math.ceil(count / USER_MANAGEMENT_PAGE_SIZE));
  }, [totalCount, usersResponse?.count]);

  const pageIndex = Math.min(page, totalPages);

  useEffect(() => {
    if (usersResponse) {
      setTotalCount(usersResponse.count);
    }
  }, [usersResponse]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchValue(searchInput);
    }, USER_MANAGEMENT_SEARCH_DEBOUNCE_MS);

    return (): void => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchValue]);

  const handleSortingChange = useCallback((nextSorting: SortingState): void => {
    setSorting(nextSorting);
    setPage(1);
  }, []);

  useEffect(() => {
    if (page !== pageIndex) {
      setPage(pageIndex);
    }
  }, [page, pageIndex]);

  const openCreate = (): void => {
    setModalMode('create');
    setActiveUser(null);
    setModalOpen(true);
  };

  const openEdit = (target: UserDto): void => {
    setModalMode('edit');
    setActiveUser(target);
    setModalOpen(true);
  };

  const invalidateUsers = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: getUsersFindAllQueryKey() });
  }, [queryClient]);

  const handleSave = useCallback(
    (values: UserFormValues): void => {
      switch (modalMode) {
        case 'edit': {
          if (!activeUser) {
            return;
          }

          updateUser(
            {
              userId: activeUser.id,
              data: {
                email: values.email,
                countryCode: values.countryCode,
                firstName: values.firstName.trim() || undefined,
                lastName: values.lastName.trim() || undefined,
                organization: values.organization.trim() || undefined,
              },
            },
            {
              onSuccess: invalidateUsers,
            },
          );
          return;
        }
        case 'create':
          createUser(
            {
              data: {
                email: values.email,
                countryCode: values.countryCode,
                firstName: values.firstName.trim() || undefined,
                lastName: values.lastName.trim() || undefined,
                organization: values.organization.trim(),
              },
            },
            {
              onSuccess: invalidateUsers,
            },
          );
          return;
        default:
          return;
      }
    },
    [activeUser, createUser, invalidateUsers, modalMode, updateUser],
  );

  const handleDelete = useCallback(
    (target: UserDto): void => {
      removeUser(
        { userId: target.id },
        {
          onSuccess: invalidateUsers,
        },
      );
    },
    [invalidateUsers, removeUser],
  );

  if (!isWeb) {
    return (
      <TabScreenContainer>
        <ScreenHeader title={t('userManagement.title')} />
        <EmptyStateView
          title={t('userManagement.noAccess.title')}
          description={t('userManagement.noAccess.description')}
        />
      </TabScreenContainer>
    );
  }

  return (
    <TabScreenContainer>
      <ScreenHeader
        title={t('userManagement.title')}
        actions={
          <Button
            title={t('userManagement.actions.add')}
            onPress={openCreate}
            disabled={!canUpdate}
          />
        }
      />

      <ScrollView>
        <YStack gap="$5" alignItems="center">
          <YStack gap="$5" width="100%">
            <YStack gap="$2">
              <SizableText fontFamily="$bold" size="$2">
                {t('userManagement.search.label')}
              </SizableText>
              <Input
                value={searchInput}
                onChangeText={setSearchInput}
                placeholder={t('userManagement.search.placeholder')}
                clearable
              />
            </YStack>

            {isPending && users.length === 0 ? (
              <LoadingView message={t('userManagement.loading')} />
            ) : (
              <UserManagementTable
                users={users}
                onEdit={canUpdate ? openEdit : undefined}
                onDelete={canUpdate ? handleDelete : undefined}
                onSortingChange={handleSortingChange}
              />
            )}

            {totalPages > 1 && (
              <Pagination
                page={pageIndex}
                count={totalPages}
                onChange={setPage}
              />
            )}
          </YStack>
        </YStack>
      </ScrollView>

      <UserManagementModal
        open={modalOpen}
        mode={modalMode}
        user={activeUser}
        countryOptions={countryOptions.filter(
          (option) => option.value !== 'all',
        )}
        isCountryLocked={isDelegate}
        lockedCountry={user?.country}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </TabScreenContainer>
  );
}
